import { beatDuration, type Clock, type TimerHost } from './clock'
import { ClickQueue, type ClickVoice } from './metronome'
import {
  activeRuns,
  createTransport,
  pause,
  play,
  positionAt,
  reachesEnd,
  seek,
  setBpm,
  setLoop,
  stop,
  tickOf,
  type Loop,
  type PlayOptions,
  type Position,
  type Run,
  type TransportState,
} from './transport'

/**
 * El motor del transporte: une el estado (transport.ts) con el planificador con antelación y el
 * metrónomo. Un temporizador impreciso se despierta cada 25 ms y programa en el reloj de audio los
 * clics de los próximos 200 ms (ADR 0002). Cada cambio cancela lo programado a partir del
 * instante en que se aplica. Reloj, temporizador y clic se inyectan: las pruebas usan un reloj
 * simulado.
 */
export interface ClickRequest {
  time: number
  accent: boolean
  countIn: boolean
}

export type ClickSink = (click: ClickRequest) => ClickVoice | null

export interface EngineOptions {
  clock: Clock
  timer: TimerHost
  click: ClickSink
  bpm: number
  beatsPerBar: number
  /** Duración de la canción en pulsos: al llegar, se para. */
  total: number
  lookahead?: number
  interval?: number
}

export interface EngineStats {
  scheduled: number
  /** Clics que ya habían pasado al despertar (tras una suspensión) y no se tocaron tarde. */
  missed: number
  minLead: number
  maxTickGap: number
}

export type EngineEvent = 'change' | 'ended'

export class TransportEngine {
  private state: TransportState
  private readonly queue = new ClickQueue()
  private readonly next = new WeakMap<Run, number>()
  private readonly listeners = new Set<(event: EngineEvent) => void>()
  private handle: unknown = null
  private lastTick: number | null = null
  private beatsPerBar: number
  private total: number
  private metronome = true
  private stats: EngineStats = this.freshStats()
  private readonly clock: Clock
  private readonly timer: TimerHost
  private readonly click: ClickSink
  private readonly lookahead: number
  private readonly interval: number

  constructor(options: EngineOptions) {
    this.clock = options.clock
    this.timer = options.timer
    this.click = options.click
    this.lookahead = options.lookahead ?? 0.2
    this.interval = options.interval ?? 25
    this.beatsPerBar = options.beatsPerBar
    this.total = options.total
    this.state = createTransport(options.bpm)
  }

  getState(): TransportState {
    return this.state
  }

  /** Posición en un instante del reloj de audio. La interfaz pasa el instante que se oye. */
  position(time: number): Position {
    return positionAt(this.state, time)
  }

  subscribe(listener: (event: EngineEvent) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getStats(): EngineStats {
    return { ...this.stats }
  }

  play(options: PlayOptions): void {
    if (this.state.status === 'running') return
    this.stats = this.freshStats()
    this.lastTick = null
    this.update(play(this.state, this.clock.now(), options))
  }

  pause(heard: number): void {
    this.update(pause(this.state, heard))
  }

  stop(): void {
    this.update(stop(this.state))
  }

  seek(beat: number): void {
    this.update(seek(this.state, this.clock.now(), beat))
  }

  setBpm(bpm: number): void {
    this.update(setBpm(this.state, this.clock.now(), bpm))
  }

  setLoop(loop: Loop | null): void {
    this.update(setLoop(this.state, this.clock.now(), loop))
  }

  /** Compás y duración de la canción (cambian si se edita el mapa de tiempos). */
  configure(options: { beatsPerBar: number; total: number }): void {
    this.beatsPerBar = options.beatsPerBar
    this.total = options.total
  }

  /** Activa o silencia el metrónomo durante la canción. La cuenta de entrada siempre suena. */
  setMetronome(enabled: boolean): void {
    if (enabled === this.metronome) return
    this.metronome = enabled
    if (this.state.status === 'running') {
      this.cancelFrom(this.clock.now())
      this.schedule()
    }
  }

  dispose(): void {
    this.stopTimer()
    this.queue.cancelPending(this.clock.now())
    this.listeners.clear()
  }

  private freshStats(): EngineStats {
    return { scheduled: 0, missed: 0, minLead: Number.POSITIVE_INFINITY, maxTickGap: 0 }
  }

  private update(next: TransportState): void {
    const previous = this.state
    if (next === previous) return
    this.state = next
    if (next.status !== 'running') {
      this.stopTimer()
      this.queue.cancelPending(this.clock.now())
    } else {
      // Lo programado desde que empieza a mandar el tramo nuevo ya no vale.
      const opening = next.run.time - next.run.countIn * beatDuration(next.run.bpm)
      if (previous.status === 'running') this.cancelFrom(opening)
      this.startTimer()
      this.schedule()
    }
    this.emit('change')
  }

  private cancelFrom(time: number): void {
    this.queue.cancelFrom(time)
    for (const { run } of activeRuns(this.state)) {
      const known = this.next.get(run)
      if (known === undefined) continue
      const first = Math.max(
        -run.countIn,
        Math.ceil((time - run.time) / beatDuration(run.bpm) - 1e-9),
      )
      this.next.set(run, Math.min(known, first))
    }
  }

  private startTimer(): void {
    if (this.handle !== null) return
    this.handle = this.timer.setInterval(() => this.schedule(), this.interval)
  }

  private stopTimer(): void {
    if (this.handle === null) return
    this.timer.clearInterval(this.handle)
    this.handle = null
  }

  private schedule(): void {
    const state = this.state
    if (state.status !== 'running') return
    const now = this.clock.now()
    if (this.lastTick !== null) {
      this.stats.maxTickGap = Math.max(this.stats.maxTickGap, now - this.lastTick)
    }
    this.lastTick = now

    if (reachesEnd(state, now, this.total)) {
      this.update(stop(state))
      this.emit('ended')
      return
    }

    const horizon = now + this.lookahead
    for (const { run, until } of activeRuns(state)) {
      let index = this.next.get(run) ?? -run.countIn
      for (;;) {
        const tick = tickOf(run, index)
        if (tick.time >= until || tick.time >= horizon) break
        index += 1
        // Un clic que ya pasó no se toca tarde: se salta y se cuenta (ADR 0002).
        if (tick.time < now) {
          this.stats.missed += 1
          continue
        }
        if (!tick.countIn && (tick.beat >= this.total || !this.metronome)) continue
        const accent = tick.countIn
          ? (tick.index + run.countIn) % this.beatsPerBar === 0
          : tick.beat % this.beatsPerBar === 0
        const voice = this.click({ time: tick.time, accent, countIn: tick.countIn })
        this.stats.scheduled += 1
        this.stats.minLead = Math.min(this.stats.minLead, tick.time - now)
        if (voice) this.queue.add(voice)
      }
      this.next.set(run, index)
    }
    this.queue.prune(now)
  }

  private emit(event: EngineEvent): void {
    this.listeners.forEach((listener) => listener(event))
  }
}
